import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Input,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import { SharedService } from "../../../../../core/services/shared.service";
import { ChartModule } from "primeng/chart";
import { QR3ReportsDto } from "../../../../../core/models/dashboard/qr3ReportsDto";
import { GetOpenClientCreditsResponse } from "../../../../../core/models/reports/getOpenClientCreditsResponse";

@Component({
  selector: "jni-bar-chart",
  templateUrl: "./bar-chart.component.html",
  styleUrl: "./bar-chart.component.scss",
  imports: [ChartModule],
})
export class BarChartComponent implements OnInit {
  @Input() type = "";
  @Input() recordList: QR3ReportsDto[] | GetOpenClientCreditsResponse[] = [];
  @Input() title = "";
  @Input() xAxisTitle = "";
  @Input() yAxisTitle = "";

  basicData!: {
    labels: string[];
    datasets: {
      label?: string;
      data: unknown[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  };

  basicOptions!: {
    responsive: boolean;
    plugins: {
      legend: {
        labels: {
          color: string;
        };
        position?: string;
        display?: boolean;
      };
      title?: {
        display: boolean;
        text: string;
        font?: { size: number; weight: string };
      };
    };
    scales: {
      x: {
        ticks: {
          color: string;
          maxRotation?: number;
          minRotation?: number;
        };
        grid: {
          color: string;
        };
        title?: {
          display: boolean;
          text: string;
          font?: { size: number; weight: string };
        };
      };
      y: {
        beginAtZero: boolean;
        ticks: {
          color: string;
          stepSize?: number;
          callback?: (value: number) => number | string | undefined;
        };
        grid: {
          color: string;
        };
        title?: {
          display: boolean;
          text: string;
          font?: { size: number; weight: string };
        };
      };
    };
  };

  platformId = inject(PLATFORM_ID);
  bgColor!: string;

  private sharedService = inject(SharedService);

  constructor(private cd: ChangeDetectorRef) {
    effect(() => {
      const currentTheme = this.sharedService.getTheme()();
      this.bgColor = currentTheme === "dark" ? "#212630" : "#FFFFFF";
      this.ngOnInit();
    });
  }

  ngOnInit() {
    this.initChart();
  }

  initChart() {
    if (isPlatformBrowser(this.platformId)) {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue("--p-h2color-color");
      const textColorSecondary =
        documentStyle.getPropertyValue("--p-h2color-color");
      const surfaceBorder = documentStyle.getPropertyValue("--bs-border-color");

      // this.basicData = {
      //   labels: ["Q1", "Q2", "Q3", "Q4"],
      //   datasets: [
      //     {
      //       label: "Sales",
      //       data: [540, 325, 702, 620],
      //       backgroundColor: [
      //         "rgba(249, 115, 22, 0.2)",
      //         "rgba(6, 182, 212, 0.2)",
      //         "rgb(107, 114, 128, 0.2)",
      //         "rgba(139, 92, 246, 0.2)",
      //       ],
      //       borderColor: [
      //         "rgb(249, 115, 22)",
      //         "rgb(6, 182, 212)",
      //         "rgb(107, 114, 128)",
      //         "rgb(139, 92, 246)",
      //       ],
      //       borderWidth: 1,
      //     },
      //   ],
      // };

      if (this.type === "invoicePosted") {
        this.invoicePostedGraphDataSetup();
        this.invoicePostedGraphOptionsSetup(
          textColor,
          textColorSecondary,
          surfaceBorder
        );
      } else if (this.type === "clientCreditReport") {
        this.openClientCreditGraphDataSetup();
        this.openClientCreditGraphOptionSetup(
          textColor,
          textColorSecondary,
          surfaceBorder
        );
      }

      this.cd.markForCheck();
    }
  }

  invoicePostedGraphDataSetup() {
    const counts = (this.recordList as unknown[] as QR3ReportsDto[]).reduce(
      (acc, inv) => {
        acc[inv.jobName] = (acc[inv.jobName] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const top10 = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    const labels = top10.map(([job]) =>
      job.length > 12 ? `${job.substring(0, 20)}...` : job
    );
    const data = top10.map(([, count]) => count);
    const backgroundColors = labels.map(() => this.randomColor());

    this.basicData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderColor: [],
          borderWidth: 0,
        },
      ],
    };
  }

  randomColor(): string {
    return `#${Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")}`;
  }

  openClientCreditGraphDataSetup() {
    const customerGroups = (
      this.recordList as unknown[] as GetOpenClientCreditsResponse[]
    ).reduce(
      (acc, curr) => {
        const key = curr.customerAccount;
        if (!acc[key]) {
          acc[key] = {
            customerName: curr.customerName,
            totalAmount: 0,
          };
        }
        acc[key].totalAmount += curr.amountNotSettled ?? 0;
        return acc;
      },
      {} as Record<string, { customerName: string; totalAmount: number }>
    );

    const top10Data = Object.entries(customerGroups)
      .map(([customerAccount, data]) => ({
        customerAccount,
        customerName: data.customerName,
        totalAmount: Number(Math.abs(data.totalAmount).toFixed(2)),
      }))
      .sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount))
      .slice(0, 15);

    const sortedData = top10Data.sort(() => Math.random() - 0.5);
    const labels = sortedData.map(item =>
      item.customerName.length > 12
        ? `${item.customerName.substring(0, 12)}...`
        : item.customerName
    );
    const data = sortedData.map(item => item.totalAmount);
    const backgroundColors = labels.map(() => this.randomColor());

    this.basicData = {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColors,
          borderColor: [],
          borderWidth: 0,
        },
      ],
    };
  }

  invoicePostedGraphOptionsSetup(
    textColor: string,
    textColorSecondary: string,
    surfaceBorder: string
  ) {
    this.basicOptions = {
      responsive: true,
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
          position: "top",
          display: false,
        },
        title: {
          display: true,
          text: this.title,
          font: {
            size: 18,
            weight: "bold",
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
          title: {
            display: true,
            text: this.xAxisTitle,
            font: { size: 16, weight: "bold" },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary,
            callback: function (value) {
              if (Number.isInteger(value)) {
                return value;
              }
              return undefined;
            },
            stepSize: 1,
          },
          grid: {
            color: surfaceBorder,
          },
          title: {
            display: true,
            text: this.yAxisTitle,
            font: { size: 16, weight: "bold" },
          },
        },
      },
    };
  }

  openClientCreditGraphOptionSetup(
    textColor: string,
    textColorSecondary: string,
    surfaceBorder: string
  ) {
    this.basicOptions = {
      responsive: true,
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
          position: "top",
          display: false,
        },
        title: {
          display: true,
          text: this.title,
          font: {
            size: 18,
            weight: "bold",
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            maxRotation: 45,
            minRotation: 45,
          },
          grid: {
            color: surfaceBorder,
          },
          title: {
            display: true,
            text: this.xAxisTitle,
            font: { size: 16, weight: "bold" },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary,
            callback: (value: number) => {
              return `$${value.toFixed(2)}`;
            },
          },
          grid: {
            color: surfaceBorder,
          },
          title: {
            display: true,
            text: this.yAxisTitle,
            font: { size: 16, weight: "bold" },
          },
        },
      },
    };
  }
}
